import React from 'react';
import { IconV2 } from '@withjoy/joykit';
import type { IconV2Props } from '@withjoy/joykit/components/IconV2/Icon.types';

/**
 * The product picker's own glyph set, drawn by design for this control (see
 * the PDP product-selector thread). They are stroke icons on a 24-grid, so
 * they live beside the picker rather than in joykit's filled IconKit set.
 * Strokes take `currentColor`, so the row's text color paints them.
 */
type PickerIconProps = Omit<IconV2Props, 'viewBox' | 'children'>;

const StrokeIcon = ({ children, ...props }: PickerIconProps & { children: React.ReactNode }) => (
  <IconV2
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <g
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </g>
  </IconV2>
);

/** Envelope with a heart seal: the classic 5x7 card. */
export const ClassicPaperIcon = (props: PickerIconProps) => (
  <StrokeIcon {...props}>
    <path d="M6.5 11.5V4.5A1.5 1.5 0 0 1 8 3h8a1.5 1.5 0 0 1 1.5 1.5v7" />
    <path
      transform="translate(12 8.8)"
      d="M0 2.2c-1.9-1.2-3-2.2-3-3.4a1.5 1.5 0 0 1 3-.6 1.5 1.5 0 0 1 3 .6c0 1.2-1.1 2.2-3 3.4z"
    />
    <path d="M6.5 8.6 3.6 10.7a1.5 1.5 0 0 0-.6 1.2v7.6A1.5 1.5 0 0 0 4.5 21h15a1.5 1.5 0 0 0 1.5-1.5v-7.6a1.5 1.5 0 0 0-.6-1.2l-2.9-2.1" />
    <path d="M3.5 20.4 10.4 15a2.6 2.6 0 0 1 3.2 0l6.9 5.4M3.4 11.2l5.8 4.2M20.6 11.2l-5.8 4.2" />
  </StrokeIcon>
);

/** Landscape postcard: address lines and a stamp. */
export const PostcardIcon = (props: PickerIconProps) => (
  <StrokeIcon {...props}>
    <rect
      x="2.5"
      y="5"
      width="19"
      height="14"
      rx="2"
    />
    <path d="M12 8v8" />
    <rect
      x="15"
      y="8"
      width="3.5"
      height="4"
      rx=".5"
    />
    <path d="M5.5 11h3.5M5.5 13.5h2.25M14.75 15h4" />
  </StrokeIcon>
);

/** Horseshoe magnet, tilted. */
export const MagnetIcon = (props: PickerIconProps) => (
  <StrokeIcon {...props}>
    <g
      transform="rotate(45 12 12) translate(12 12.25) scale(1.08) translate(-12 -11.25)"
      strokeWidth={1.39}
    >
      <path d="M6 4.5V12a6 6 0 0 0 12 0V4.5h-4V12a2 2 0 0 1-4 0V4.5z" />
      <path d="M6 8h4M14 8h4" />
    </g>
  </StrokeIcon>
);

/** Phone with a gem badge over its corner (the phone is masked out behind the gem). */
export const PremiumDigitalIcon = (props: PickerIconProps) => (
  <StrokeIcon {...props}>
    <defs>
      <mask id="joy-product-picker-premium-gem-mask">
        <rect
          width="24"
          height="24"
          fill="#fff"
        />
        <g
          fill="#000"
          stroke="#000"
          strokeWidth={3}
        >
          <g
            transform="translate(16.6 15.7) scale(0.48) translate(-16 -16)"
            strokeWidth={3.12}
          >
            <path d="M8.6 5.7h14.8l5.9 7.4L16 26.4 2.7 13.1z" />
            <path d="M2.7 13.1h26.6" />
            <path
              strokeWidth={2.5}
              d="M14.2 5.7l-2.9 7.4L16 24.2l4.7-11.1-2.9-7.4"
            />
          </g>
        </g>
      </mask>
    </defs>
    <g mask="url(#joy-product-picker-premium-gem-mask)">
      <rect
        x="3.5"
        y="2.5"
        width="12"
        height="19"
        rx="2.5"
      />
      <path d="M8 5h3" />
    </g>
    <g
      transform="translate(16.6 15.7) scale(0.48) translate(-16 -16)"
      strokeWidth={3.12}
    >
      <path d="M8.6 5.7h14.8l5.9 7.4L16 26.4 2.7 13.1z" />
      <path d="M2.7 13.1h26.6" />
      <path
        strokeWidth={2.5}
        d="M14.2 5.7l-2.9 7.4L16 24.2l4.7-11.1-2.9-7.4"
      />
    </g>
  </StrokeIcon>
);

/** Plain phone. */
export const BasicDigitalIcon = (props: PickerIconProps) => (
  <StrokeIcon {...props}>
    <rect
      x="6"
      y="2.5"
      width="12"
      height="19"
      rx="2.5"
    />
    <path d="M10.5 5h3" />
  </StrokeIcon>
);
